import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

// b7 custom: 터치에서 "가로 드래그"로 판정하기 위한 최소 이동량(px).
const B7_TOUCH_DRAG_THRESHOLD = 8;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      // b7 custom: 터치/펜 드래그 진입점 해제
      scroller.removeEventListener('pointerdown', this.#handlePointerDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // b7 custom start
    // ★ 루프(마지막→처음, 처음→마지막)는 전용 루틴 하나로만 처리한다 ★
    // 드래그·화살표·autoplay 가 전부 이 한 경로를 타게 해서 방향과 무관하게 동일하게 만든다.
    const isLoop = requestedIndex < 0 || requestedIndex > lastIndex;

    if (isLoop && !instant && visibleSlides.length <= 1) {
      await this.#b7LoopTo(requestedIndex < 0 ? -1 : 1, event != null);
      return;
    }

    // 루프가 아닌 다중 점프(썸네일/도트로 멀리 이동)는 기존 재배치 트릭 유지
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      this.#scroll.snap = false;

      const edge = this.#scroll.axis === 'y' ? 'top' : 'left';

      // b7 custom: DOM 을 건드리기 전에 먼저 잰다 (#b7LoopTo 주석 참고)
      const positionBefore = currentSlide.getBoundingClientRect()[edge];

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      placeholder.setAttribute('aria-hidden', 'true');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      const drift = currentSlide.getBoundingClientRect()[edge] - positionBefore;
      this.#b7ScrollByInstant(drift);

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        placeholder.replaceWith(targetSlide);
        this.#scroll.to(targetSlide, { instant: true });
        this.#scroll.snap = true;

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }
    // b7 custom end

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      // b7 custom: iOS 는 탭 후 :hover 가 남아있어 autoplay 가 영구 정지된다.
      // 실제 마우스가 있는 환경에서만 :hover 를 검사한다.
      if ((Slideshow.canHover && this.matches(':hover')) || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * b7 custom: 실제 hover 가 가능한 기기인지 여부 (마우스 존재 여부)
   * @type {boolean}
   */
  static get canHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    // b7 custom: 터치/펜도 PC 와 완전히 동일한 드래그 로직을 타게 한다
    scroller.addEventListener('pointerdown', this.#handlePointerDown);

    // b7 custom: 터치 기기에서 mouseenter 가 한 번 발생하면 mouseleave 가 오지 않아
    // autoplay 가 영구히 멈춘다. 실제 마우스가 있는 환경에서만 바인딩한다.
    if (Slideshow.canHover) {
      this.addEventListener('mouseenter', this.suspend);
      this.addEventListener('mouseleave', this.resume);
    }

    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    // b7 custom start
    // ★ 모바일/PC 통일의 핵심 ★
    // 브라우저의 네이티브 터치 가로 스와이프를 끈다. 그러면 모바일 스와이프도
    // pointermove 로 우리 코드가 처리하게 되어, 1→2 든 5→1 이든 완전히 같은
    // 경로를 타고 같은 이징으로 움직인다. pan-y 로 두면 세로 페이지 스크롤은 유지된다.
    if (!this.disabled && !this.isNested) {
      // scroller.style.touchAction = this.#scroll.axis === 'y' ? 'pan-x' : 'pan-y';
      // scroller.style.overscrollBehavior = 'contain';

      scroller.style.touchAction =
        this.#scroll.axis === 'y'
          ? 'pan-x'
          : 'pan-y';

      if (this.#scroll.axis === 'x') {
        scroller.style.overscrollBehaviorX = 'contain';
        scroller.style.overscrollBehaviorY = 'auto';
      } else {
        scroller.style.overscrollBehaviorY = 'contain';
        scroller.style.overscrollBehaviorX = 'auto';
      }
    }
    // b7 custom end

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * b7 custom: 스크롤 애니메이션이 실제로 끝날 때까지 기다린다.
   * scrollend 미지원 브라우저를 위해 타임아웃 폴백을 둔다.
   * @param {number} [timeout]
   */
  #b7WaitForScrollEnd(timeout = 800) {
    const { scroller } = this.refs;

    return new Promise((resolve) => {
      /** @type {number} */
      let timer;
      const done = () => {
        clearTimeout(timer);
        scroller.removeEventListener('scrollend', done);
        resolve(undefined);
      };

      scroller.addEventListener('scrollend', done, { once: true });
      timer = setTimeout(done, timeout);
    });
  }

  /**
   * b7 custom: CSS `scroll-behavior: smooth` 의 영향을 받지 않는 "즉시" 스크롤 보정.
   *
   * scroller 에 smooth 가 걸려 있으면 scrollLeft 에 그냥 대입해도 점프가 아니라
   * 애니메이션이 된다. 루프 중 위치 보정은 반드시 즉시 반영돼야 하므로
   * behavior:'instant' 를 명시해 CSS 설정을 무시한다.
   *
   * @param {number} delta
   */
  #b7ScrollByInstant(delta) {
    if (!delta || !this.#scroll) return;

    const { scroller } = this.refs;
    const vertical = this.#scroll.axis === 'y';
    const target = (vertical ? scroller.scrollTop : scroller.scrollLeft) + delta;

    scroller.scrollTo(
      /** @type {ScrollToOptions} */ (
        vertical ? { top: target, behavior: 'instant' } : { left: target, behavior: 'instant' }
      )
    );
  }

  /**
   * b7 custom: 마지막↔처음 루프 전용 루틴. 앞/뒤 방향이 완전히 대칭이다.
   *
   * @param {number} direction 1 = 마지막→처음, -1 = 처음→마지막
   * @param {boolean} [userInitiated]
   */
  async #b7LoopTo(direction, userInitiated = false) {
    if (this.#disabled || !this.#scroll) return;

    const slides = this.slides;
    if (!slides || slides.length <= 1) return;

    const { scroller } = this.refs;
    const previousIndex = this.current;
    const currentSlide = slides[previousIndex];
    const targetIndex = direction > 0 ? 0 : slides.length - 1;
    const targetSlide = slides[targetIndex];

    if (!currentSlide || !targetSlide || currentSlide === targetSlide) return;

    const edge = this.#scroll.axis === 'y' ? 'top' : 'left';
    const scrollProp = this.#scroll.axis === 'y' ? 'scrollTop' : 'scrollLeft';

    this.#disabled = true;
    this.setAttribute('transitioning', '');

    // 스냅은 이 루틴이 끝날 때까지 계속 꺼둔다
    this.#scroll.snap = false;

    // 직전 스크롤(드래그 관성 등)이 잦아들 때까지 한 프레임 대기
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // ★ 반드시 DOM 을 건드리기 "전" 에 현재 슬라이드 위치를 잰다 ★
    // placeholder 를 먼저 넣고 재면, 마지막→처음 방향일 때 placeholder 가 맨 앞에
    // 삽입되면서 콘텐츠가 이미 한 칸 밀린 상태를 기준으로 재게 되어 보정값이 틀어진다.
    // (그래서 5→1 만 두 칸을 스크롤하며 이상하게 넘어갔다)
    const positionBefore = currentSlide.getBoundingClientRect()[edge];

    // 타깃의 원래 자리를 잡아둘 placeholder
    const placeholder = document.createElement('slideshow-slide');
    placeholder.setAttribute('aria-hidden', 'true');
    targetSlide.before(placeholder);

    // 타깃을 현재 슬라이드 바로 옆으로
    if (direction > 0) currentSlide.after(targetSlide);
    else currentSlide.before(targetSlide);

    // ★ 위치 보정 ★ 옮기면서 어긋난 만큼 스크롤을 되돌린다 (반드시 즉시 반영)
    const drift = currentSlide.getBoundingClientRect()[edge] - positionBefore;
    this.#b7ScrollByInstant(drift);

    targetSlide.setAttribute('aria-hidden', 'false');

    // 보정한 위치를 브라우저에 확정시킨 뒤 다음 프레임에 애니메이션을 시작한다
    void scroller[scrollProp];
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // 한 칸만 부드럽게 이동
    this.#scroll.to(targetSlide);
    await this.#b7WaitForScrollEnd();

    // ── DOM 복구 ── 이동 전후 위치 차이를 직접 재서 같은 프레임 안에 즉시 보정
    const restorePositionBefore = targetSlide.getBoundingClientRect()[edge];
    placeholder.replaceWith(targetSlide);
    const restoreDrift = targetSlide.getBoundingClientRect()[edge] - restorePositionBefore;
    this.#b7ScrollByInstant(restoreDrift);

    this.#disabled = false;
    this.removeAttribute('transitioning');

    // 보정이 화면에 반영된 다음 프레임에 스냅을 켠다 (같은 프레임에 켜면 다시 튄다)
    await new Promise((resolve) => requestAnimationFrame(resolve));
    this.#scroll.snap = true;

    this.current = targetIndex;
    this.#centerSelectedThumbnail(targetIndex);

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index: targetIndex,
        previousIndex,
        userInitiated,
        trigger: 'select',
        slide: targetSlide,
        id: targetSlide.getAttribute('slide-id'),
      })
    );

    // Safari 에서 progress/timeline 이 갱신되도록 강제
    requestAnimationFrame(() => {
      this.setAttribute('refreshing-timeline', '');
      requestAnimationFrame(() => this.removeAttribute('refreshing-timeline'));
    });
  }

  /**
   * b7 custom: 드래그를 놓은 순간 "가장 가까운 슬라이드"를 기하학적으로 계산한다.
   *
   * 기존 #sync() 는 IntersectionObserver 가 채우는 visibleSlides 에만 의존한다.
   * 드래그 중에는 어떤 슬라이드도 70% 이상 보이지 않아 visibleSlides 가 비어버리고,
   * 그러면 #sync() 가 이전 index 를 그대로 돌려줘서 "절반 넘게 밀었는데 원래대로
   * 되돌아가는" 현상이 생긴다. 여기서는 전체 슬라이드를 대상으로 직접 계산한다.
   *
   * @returns {number}
   */
  #b7NearestSlideIndex() {
    const { slides } = this;
    if (!slides?.length || !this.#scroll) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = slides.map((slide) => center(slide, axis));
    const referencePoint =
      this.visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const index = centers.indexOf(closestCenter);

    return index === -1 ? this.current : index;
  }

  /**
   * b7 custom: 터치/펜 드래그 진입점.
   * 마우스는 기존 mousedown 경로가 처리하므로 여기서는 무시한다(중복 방지).
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#handleMouseDown(event);
  };

  /**
   * Handles the 'mousedown' / touch 'pointerdown' event to start dragging slides.
   * @param {MouseEvent | PointerEvent} event - The event that started the drag.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    // b7 custom: 터치 여부 판별
    const isTouch = 'pointerType' in event && event.pointerType !== 'mouse';
    const startEvent = event;

    // b7 custom: 터치에서 pointerdown 에 preventDefault 를 걸면 슬라이드 안의
    // 링크 클릭이 죽을 수 있다. 가로 스와이프는 touch-action 으로 이미 막았으므로
    // 마우스일 때만 기본 동작(텍스트/이미지 드래그)을 막는다.
    if (!isTouch) event.preventDefault();

    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const crossAxis = axis === 'x' ? 'y' : 'x';
    const startPosition = event[axis];
    const crossStartPosition = event[crossAxis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    // b7 custom: 드래그가 중간에 취소돼도 스냅이 꺼진 채 남지 않도록
    const bailOut = () => {
      controller.abort();
      this.#dragging = false;
      this.#scroll.snap = true;
    };

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        // b7 custom start
        // 터치는 방향을 확정한 뒤에만 드래그를 시작한다.
        // (세로로 밀었는데 슬라이드가 흔들리는 걸 막는다)
        if (isTouch) {
          const crossDelta = crossStartPosition - event[crossAxis];

          if (Math.abs(initialDelta) < B7_TOUCH_DRAG_THRESHOLD) return;
          if (Math.abs(initialDelta) <= Math.abs(crossDelta)) {
            bailOut();
            return;
          }
        }
        // b7 custom end

        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          bailOut();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      // b7 custom: 드래그를 시작시킨 pointerdown 이 document 까지 버블링되어
      // 방금 등록한 리스너에 그대로 잡히는 것을 막는다.
      if (event === startEvent) return;

      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) {
        this.#scroll.snap = true;
        return;
      }

      // b7 custom start
      const direction = Math.sign(velocity);

      // visibleSlides 대신 기하학적 계산을 쓴다 (#b7NearestSlideIndex 주석 참고)
      const next = (this.current = this.#b7NearestSlideIndex());

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;

      // clamp 하지 않은 원래 요청 index. 예: 마지막 5번에서 다음 → slides.length
      const requestedIndex = next + modifier;
      const lastIndex = slides.length - 1;

      this.removeAttribute('dragging');

      if (moved) {
        try {
          this.releasePointerCapture(event.pointerId);
        } catch {
          /* 이미 해제된 경우 무시 */
        }
      }

      if (requestedIndex < 0 || requestedIndex > lastIndex) {
        // ── 루프 구간(5→1, 1→5) ── select() 가 #b7LoopTo 로 위임한다
        await this.select(requestedIndex, event);
      } else {
        // ── 일반 구간(1→2, 2→3 …) ──
        // select() 는 index 가 그대로면 아무것도 하지 않고 반환한다.
        // 그러면 손가락을 뗀 위치에 멈춰 있다가 스냅이 켜지는 순간 툭 튄다.
        // 원본 테마처럼 항상 스크롤을 실행해서 되돌아가는 것도 애니메이션이 되게 한다.
        const newSlide = slides[requestedIndex];
        const previousIndex = this.current;

        if (newSlide) {
          this.#scroll.to(newSlide);
          this.current = requestedIndex;
          this.#centerSelectedThumbnail(requestedIndex);

          if (previousIndex !== requestedIndex) {
            this.dispatchEvent(
              new SlideshowSelectEvent({
                index: requestedIndex,
                previousIndex,
                userInitiated: true,
                trigger: 'drag',
                slide: newSlide,
                id: newSlide.getAttribute('slide-id'),
              })
            );
          }
        }
      }

      await this.#scroll.finished;

      // 드래그가 다시 시작됐다면 종료
      if (this.#dragging) return;

      // ★ 스냅은 스크롤이 완전히 끝난 뒤에 복구한다 ★
      this.#scroll.snap = true;

      this.resume();
      // b7 custom end
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}